import React, { useState, useEffect, useContext } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import Layout from '../../../components/public/common/Layout';
import PageHeader from '../../../components/public/common/PageHeader';
import ServicesGrid from '../../../components/public/services/ServicesGrid';
import ServiceCategories from '../../../components/public/services/ServiceCategories';
import ContactCTA from '../../../components/public/common/ContactCTA';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import TechLoader from '../../../components/common/TechLoader';
import { fetchPublishedServices } from '../../../services/serviceService';

const ServicePage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [services, setServices] = useState([]);
  const [error, setError] = useState(null);
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    const loadServices = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await fetchPublishedServices();
        
        if (response.status === 'success') {
          setServices(response.data);
        } else {
          setError(response.message || 'Failed to load services');
        }
      } catch (error) {
        console.error('Error loading services:', error);
        setError('Failed to load services. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    loadServices();
  }, []);

  // Define base section themes
  const baseSectionThemes = {
    serviceCategories: 'light',
    servicesDetail: 'dark',
    contact: 'dark'
  };

  // Toggle themes if global theme is dark
  const sectionThemes = theme === 'dark'
    ? Object.fromEntries(
        Object.entries(baseSectionThemes).map(([key, value]) => [
          key,
          value === 'dark' ? 'light' : 'dark'
        ])
      )
    : baseSectionThemes;

  if (isLoading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
          <TechLoader size="xl" text="Loading our services..." />
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Error</h1>
            <p className="text-gray-600 dark:text-gray-300">{error}</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <HelmetProvider>
      <Layout>
        <Helmet>
          <title>Our Services | CodeITronics</title>
          <meta name="description" content="Explore our comprehensive range of technology solutions including software development, AI solutions, mobile apps, DevOps, e-commerce and more." />
          <meta property="og:title" content="Our Services | CodeITronics" />
          <meta property="og:description" content="Explore our comprehensive range of technology solutions including software development, AI solutions, mobile apps, DevOps, e-commerce and more." />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={window.location.href} />
        </Helmet>
        
        <PageHeader 
          title="Our Services" 
          subtitle="Comprehensive technology solutions to help your business innovate and grow"
          backgroundImage="/images/services-header.jpg"
        />
        
        <ServiceCategories theme={sectionThemes.serviceCategories} />
        
        <ServicesGrid 
          theme={sectionThemes.servicesDetail} 
          services={services}
        />
        
        <ContactCTA 
          title="Ready to innovate with us?" 
          subtitle="Get in touch today to discuss how we can help transform your business with our technology solutions."
          buttonText="Contact Us"
          buttonLink="/contact"
          theme={sectionThemes.contact}
        />
      </Layout>
    </HelmetProvider>
  );
};

export default ServicePage; 